import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API_BASE,
    headers: { "Content-Type": "application/json" },
});

// Attach auth token to every request when available
api.interceptors.request.use((cfg) => {
    const token = localStorage.getItem("naami_admin_token");
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
});

// Public
export const fetchBrands = async () => (await api.get("/brands")).data;
export const hubSetBrandClosed = async (brandId, closed, reopen_at = null) => {
    const staff = JSON.parse(localStorage.getItem("naami_staff") || "null");
    return (await api.post(`/hub/brands/${brandId}/closed`, { closed, reopen_at }, { headers: { "X-Staff-Token": staff?.token || "" } })).data;
};
export const fetchTrackBoard = async () => (await api.get("/track/board")).data;
export const fetchMemberSavings = async (memberId) => (await api.get(`/loyalty/${memberId}/savings`)).data;
export const fetchClubMemberDiscount = async () => (await api.get("/club/member-discount")).data;
export const fetchBrand = async (slug) => (await api.get(`/brands/${slug}`)).data;
export const createOrder = async (body) => (await api.post("/orders", body)).data;
export const fetchOrders = async (status = "all", scope = "active") =>
    (await api.get(`/orders`, { params: { status, today: true, scope } })).data;
export const fetchOrder = async (id) => (await api.get(`/orders/${id}`)).data;
export const markCustomerArrived = async (id) => (await api.post(`/orders/${id}/arrived`)).data;
export const acknowledgeArrival = async (id) => (await api.post(`/orders/${id}/acknowledge-arrival`)).data;
export const releasePreorder = async (id) => (await api.post(`/orders/${id}/release`)).data;

// ----- Gophr delivery -----
export const bookCourier = async (id) => (await api.post(`/delivery/orders/${id}/book`)).data;
export const cancelCourier = async (id) => (await api.post(`/delivery/orders/${id}/cancel`)).data;
export const fetchTracking = async (id) => (await api.get(`/delivery/orders/${id}/tracking`)).data;
export const updateOrderStatus = async (id, status) =>
    (await api.patch(`/orders/${id}/status`, { status })).data;
export const fetchStats = async () => {
    let token = "";
    try { token = JSON.parse(localStorage.getItem("naami_staff") || "null")?.token || ""; } catch { /* ignore */ }
    return (await api.get("/orders/stats", { headers: token ? { "X-Staff-Token": token } : {} })).data;
};
export const adminSetStaffPin = async (role, pin) => (await api.put("/admin/staff-pin", { role, pin })).data;
export const staffLogin = async (pin) => (await api.post("/staff/login", { pin })).data;
// Stripe Terminal (kiosk card readers)
export const adminTerminalReaders = async () => (await api.get("/admin/terminal/readers")).data;
export const adminRegisterReader = async (registration_code, label) =>
    (await api.post("/admin/terminal/readers", { registration_code, label })).data;
export const adminRemoveReader = async (id) => (await api.delete(`/admin/terminal/readers/${id}`)).data;
export const kioskTerminalReaders = async (staffToken) =>
    (await api.get("/kiosk/terminal/readers", { headers: { "X-Staff-Token": staffToken } })).data;
export const kioskPay = async (reader_id, order) => (await api.post("/kiosk/terminal/pay", { reader_id, order })).data;
export const kioskDefaultReader = async () => (await api.get("/kiosk/terminal/default")).data;
export const kioskSetDefaultReader = async (staffToken, reader) =>
    (await api.post("/kiosk/terminal/default", reader, { headers: { "X-Staff-Token": staffToken } })).data;
export const kioskPayStatus = async (id) => (await api.get(`/kiosk/terminal/pay/${id}`)).data;
export const kioskPayRetry = async (id) => (await api.post(`/kiosk/terminal/pay/${id}/retry`)).data;
export const kioskPayCancel = async (id) => (await api.post(`/kiosk/terminal/pay/${id}/cancel`)).data;

// Admin auth
export const adminLogin = async (email, password) =>
    (await api.post("/auth/login", { email, password })).data;
export const adminMe = async () => (await api.get("/auth/me")).data;

// Admin: users
export const adminListUsers = async () => (await api.get("/admin/users")).data;
export const adminCreateUser = async (body) => (await api.post("/admin/users", body)).data;
export const adminUpdateUser = async (id, body) => (await api.patch(`/admin/users/${id}`, body)).data;
export const adminDeleteUser = async (id) => (await api.delete(`/admin/users/${id}`)).data;

// Admin: brands
export const adminCreateBrand = async (body) => (await api.post("/admin/brands", body)).data;
export const adminUpdateBrand = async (id, body) => (await api.patch(`/admin/brands/${id}`, body)).data;
export const adminDeleteBrand = async (id) => (await api.delete(`/admin/brands/${id}`)).data;

// Admin: items
export const adminAddItem = async (brandId, body) =>
    (await api.post(`/admin/brands/${brandId}/items`, body)).data;
export const adminUpdateItem = async (brandId, itemId, body) =>
    (await api.patch(`/admin/brands/${brandId}/items/${itemId}`, body)).data;
export const adminDeleteItem = async (brandId, itemId) =>
    (await api.delete(`/admin/brands/${brandId}/items/${itemId}`)).data;
export const adminBulkAddItems = async (brandId, items, replace = false) =>
    (await api.post(`/admin/brands/${brandId}/items/bulk`, { items, replace })).data;

// Admin: AI extraction
export const adminExtractMenu = async (brandId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post(`/admin/brands/${brandId}/extract-menu`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
    });
    return r.data;
};

// Admin: AI auto-categorise existing items for a brand
export const adminAutoCategorise = async (brandId, overwrite = false) =>
    (await api.post(`/admin/brands/${brandId}/auto-categorise`, null, {
        params: { overwrite },
        timeout: 120000,
    })).data;

// Admin: URL menu import (Just Eat / Deliveroo / any restaurant site)
export const adminImportMenuFromUrl = async (brandId, url) =>
    (await api.post(`/admin/brands/${brandId}/import-menu-from-url`,
        { url }, { timeout: 180000 })).data;

export const adminListSavedOptions = async (brandId) =>
    (await api.get("/admin/modifier-library", { params: { brand_id: brandId } })).data;
export const adminCreateSavedOption = async (body) => (await api.post("/admin/modifier-library", body)).data;
export const adminUpdateSavedOption = async (id, body) => (await api.put(`/admin/modifier-library/${id}`, body)).data;
export const adminDeleteSavedOption = async (id) => (await api.delete(`/admin/modifier-library/${id}`)).data;
export const adminApplySavedOption = async (id, brand_id, item_ids) =>
    (await api.post(`/admin/modifier-library/${id}/apply`, { brand_id, item_ids })).data;
export const adminRemoveSavedOption = async (id, brand_id, item_ids) =>
    (await api.post(`/admin/modifier-library/${id}/remove`, { brand_id, item_ids })).data;
export const adminImportMenuCommit = async (brandId, items) =>
    (await api.post(`/admin/brands/${brandId}/import-menu-commit`,
        { items }, { timeout: 180000 })).data;

// Admin: reorder items in a brand (full list of ids in new order)
export const adminReorderItems = async (brandId, itemIds) =>
    (await api.post(`/admin/brands/${brandId}/reorder-items`, { item_ids: itemIds })).data;

// ----- Business settings (public + admin) -----
export const fetchBusinessSettings = async () => (await api.get("/business-settings")).data;
export const adminGetBusinessSettings = async () => (await api.get("/admin/business-settings")).data;
export const adminUpdateBusinessSettings = async (body) =>
    (await api.patch("/admin/business-settings", body)).data;

// ----- Epson Server Direct Print & Star CloudPRNT admin -----
export const adminPrintTest = async (printerId = "counter", kind = "epson_sdp") =>
    (await api.post(`/admin/print/test`, null, { params: { printer_id: printerId, kind } })).data;
export const adminPrintClearQueue = async (printerId = null) =>
    (await api.delete(`/admin/print/queue`, { params: { printer_id: printerId || undefined } })).data;
export const adminPrintRecent = async (printerId = null, limit = 20) =>
    (await api.get(`/admin/print/recent`, { params: { printer_id: printerId || undefined, limit } })).data;
export const adminPrintReprint = async (jobId) =>
    (await api.post(`/admin/print/reprint`, null, { params: { job_id: jobId } })).data;

// ----- Print station (Windows + Chrome --kiosk-printing auto-print) -----
export const printStationPending = async (windowMinutes = 60) =>
    (await api.get(`/print-station/pending`, { params: { window_minutes: windowMinutes } })).data;
export const printStationMarkPrinted = async (orderId) =>
    (await api.post(`/print-station/${orderId}/printed`)).data;
export const printStationReset = async (orderId) =>
    (await api.post(`/print-station/${orderId}/reset`)).data;

// ----- Discount codes -----
export const validateDiscountCode = async (code, subtotal, order_type) =>
    (await api.post("/discount/validate", { code, subtotal, order_type })).data;

// Customer Google sign-in (Emergent-managed). Token mirrored in localStorage as a header fallback.
const CUSTOMER_KEY = "naami_customer_session";
const customerCfg = () => {
    const t = localStorage.getItem(CUSTOMER_KEY);
    return { withCredentials: true, headers: t ? { "X-Customer-Session": t } : {} };
};
export const customerAuthSession = async (session_id) => {
    const d = (await api.post("/customer-auth/session", { session_id }, { withCredentials: true })).data;
    if (d.session_token) localStorage.setItem(CUSTOMER_KEY, d.session_token);
    return d;
};
export const customerMe = async () => (await api.get("/customer-auth/me", customerCfg())).data;
export const customerLogout = async () => {
    try { await api.post("/customer-auth/logout", {}, customerCfg()); } finally { localStorage.removeItem(CUSTOMER_KEY); }
};
export const adminListDiscountCodes = async () =>
    (await api.get("/admin/discount-codes")).data;
export const adminCreateDiscountCode = async (body) =>
    (await api.post("/admin/discount-codes", body)).data;
export const adminUpdateDiscountCode = async (id, body) =>
    (await api.patch(`/admin/discount-codes/${id}`, body)).data;
export const adminDeleteDiscountCode = async (id) =>
    (await api.delete(`/admin/discount-codes/${id}`)).data;

// ----- Stripe Payments -----
export const createStripeCheckout = async (orderDraft, preferredPaymentMethod = null) =>
    (await api.post("/payments/checkout/session", {
        order: orderDraft,
        origin_url: window.location.origin,
        preferred_payment_method: preferredPaymentMethod,
    })).data;

export const fetchStripeCheckoutStatus = async (sessionId) =>
    (await api.get(`/payments/checkout/status/${sessionId}`)).data;

// ----- Saved cards (loyalty members) -----
export const fetchSavedCards = async (memberId) =>
    (await api.get("/payments/saved-cards", { params: { member_id: memberId } })).data;

export const deleteSavedCard = async (pmId, memberId) =>
    (await api.delete(`/payments/saved-cards/${pmId}`, { params: { member_id: memberId } })).data;

// Loyalty + extras
export const loyaltyLookup = async (phone, email) =>
    (await api.post("/loyalty/lookup", { phone: phone || null, email: email || null })).data;
export const loyaltyJoin = async (name, phone, email, birthday, referred_by_code) =>
    (await api.post("/loyalty/join", {
        name,
        phone: phone || null,
        email: email || null,
        birthday: birthday || null,
        referred_by_code: referred_by_code || null,
    })).data;
export const loyaltyGet = async (id) => (await api.get(`/loyalty/${id}`)).data;
export const fetchXReport = async (date, endDate) => {
    const params = {};
    if (date) params.date = date;
    if (endDate) params.end_date = endDate;
    return (await api.get("/admin/x-report", { params })).data;
};
export const sendXReportWhatsapp = async (to, date) =>
    (await api.post("/admin/x-report/send-whatsapp", { to, date })).data;
export const migrateLoyaltyPhones = async () =>
    (await api.post("/admin/migrate-loyalty-phones")).data;
export const adminListLoyaltyMembers = async (params = {}) =>
    (await api.get("/admin/loyalty/members", { params })).data;
export const adminImportClubCsv = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return (await api.post("/admin/club/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
    })).data;
};
export const adminGetClubVoucherSettings = async () =>
    (await api.get("/admin/club/voucher-settings")).data;
export const adminSaveClubVoucherSettings = async (body) =>
    (await api.put("/admin/club/voucher-settings", body)).data;
export const cloudinarySignature = async (folder = "naami/items") =>
    (await api.get("/admin/cloudinary/signature", { params: { folder } })).data;
export const fetchFavourites = async (memberId, limit = 6) =>
    (await api.get(`/loyalty/${memberId}/favourites`, { params: { limit } })).data;
export const fetchLastOrders = async (memberId, limit = 3) =>
    (await api.get(`/loyalty/${memberId}/last-orders`, { params: { limit } })).data;
export const previewRedeem = async (member_id, points) =>
    (await api.post("/loyalty/preview-redeem", { member_id, points })).data;
export const fetchRelatedItems = async (itemId, limit = 4) =>
    (await api.get(`/items/${itemId}/related`, { params: { limit } })).data;
export const fetchPickupSlots = async () => (await api.get("/pickup-slots")).data;

// Reports
export const adminReportJson = async (brandId, date) =>
    (await api.get(`/admin/brands/${brandId}/report`, { params: date ? { date } : {} })).data;
export const adminReportPdfUrl = (brandId, date) => {
    const token = localStorage.getItem("naami_admin_token");
    const q = new URLSearchParams();
    if (date) q.set("date", date);
    // Token can't be in headers when opening in a new tab. We'll use blob download via api instead.
    return `${API_BASE}/admin/brands/${brandId}/report.pdf?${q.toString()}`;
};
export const adminReportPdfBlob = async (brandId, date) =>
    (await api.get(`/admin/brands/${brandId}/report.pdf`, {
        params: date ? { date } : {}, responseType: "blob",
    })).data;
export const adminSendReport = async (brandId, to, date) =>
    (await api.post(`/admin/brands/${brandId}/report/send`, { to, date })).data;

export const adminDownloadBackupBlob = async () =>
    (await api.get(`/admin/backup/download`, { responseType: "blob", timeout: 300000 })).data;

// Payout report — per-brand, date range, commission-aware.
export const adminPayoutReportJson = async (brandId, fromDate, toDate) => {
    const params = {};
    if (fromDate) params.from = fromDate;
    if (toDate) params.to = toDate;
    return (await api.get(`/admin/brands/${brandId}/payout-report`, { params })).data;
};
export const adminPayoutReportPdfBlob = async (brandId, fromDate, toDate) => {
    const params = {};
    if (fromDate) params.from = fromDate;
    if (toDate) params.to = toDate;
    return (await api.get(`/admin/brands/${brandId}/payout-report.pdf`, {
        params, responseType: "blob",
    })).data;
};

export const adminAllergens = async () => (await api.get("/admin/allergens")).data;
